import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Field, Heading, Label, Paragraph, Select, Tag } from '@digdir/designsystemet-react';
import { DownloadIcon } from '@navikt/aksel-icons';
import type { Measurement, WorkEnvironmentRisk } from '../../../api/workEnvironmentTypes';
import { useSurveyPaths } from '../../../hooks/useSurveyPaths';
import { useHmsReportPdfMutation } from '../../../hooks/mutations/useHmsReportPdfMutation';
import { measurementOptions, reportMeta, roundTags } from '../reportView';
import classes from '../WorkEnvironmentReport.module.css';

type Props = {
  surveyId: number;
  team: string;
  expected: number;
  calculated: Extract<WorkEnvironmentRisk, { calculatedAt: string }> | null;
  measurements: Measurement[];
};

export const ReportHero = ({ surveyId, team, expected, calculated, measurements }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const paths = useSurveyPaths();
  const pdf = useHmsReportPdfMutation('work-environment-report', surveyId);
  const current = measurements.find((measurement) => measurement.surveyId === surveyId);
  const tags = roundTags(current, t);
  const hasResults = calculated?.risk.status === 'assessed';

  return (
    <header className={classes.hero}>
      <div className={classes.heroInner}>
        <div className={classes.heroText}>
          <span className={`ds-label ${classes.eyebrow}`} data-size="sm">
            {t('workEnvironmentReport.eyebrow', { team })}
          </span>
          <Heading level={1} data-size="lg">
            {t('workEnvironmentReport.title')}
          </Heading>
          <div className={classes.meta}>
            {calculated && (
              <Paragraph data-size="sm" className={classes.subtle}>
                {reportMeta(calculated.calculatedAt, calculated.risk.responses, expected, t, i18n.language)}
              </Paragraph>
            )}
            {tags.map((tag) => (
              <Tag key={tag.key} data-size="sm" data-color={tag.color}>
                {tag.label}
              </Tag>
            ))}
          </div>
        </div>
        <div className={classes.tools} data-print="false">
          {measurements.length > 1 && (
            <Field className={classes.measurement}>
              <Label htmlFor="report-measurement">{t('workEnvironmentReport.hero.measurement')}</Label>
              <Select
                id="report-measurement"
                value={String(surveyId)}
                onChange={(event) => navigate(paths.report(Number(event.target.value)))}
              >
                {measurementOptions(measurements, t, i18n.language).map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Field>
          )}
          {hasResults && (
            <Button variant="secondary" loading={pdf.isPending} onClick={() => pdf.mutate()}>
              <DownloadIcon aria-hidden />
              {t('workEnvironmentReport.hero.download')}
            </Button>
          )}
        </div>
        {pdf.isError && (
          <Alert data-color="danger" data-size="sm" data-print="false" className={classes.heroAlert}>
            <Paragraph data-size="sm">{t('workEnvironmentReport.hero.pdfFailed')}</Paragraph>
          </Alert>
        )}
      </div>
    </header>
  );
};
